import { Chart } from "react-google-charts";
import { Paper, Typography, Grid, Box, useTheme } from "@mui/material";
import { useWarRoomSocket } from "../hooks/useWarRoomSocket";
import { useMemo, useState } from "react";

const STATUS_COLORS = {
  moving: "#00C853",
  idle: "#FFD600",
  ignOff: "#D50000",
  offline: "#9E9E9E",
};

// Shared pie options that adapt to light/dark mode
const getPieOptionsShared = (isDark: boolean) => ({
  pieHole: 0.35,
  is3D: true,
  pieSliceText: "value",
  pieSliceTextStyle: { fontSize: 12, color: "#fff", bold: true },
  tooltip: { 
    isHtml: true, 
    trigger: "hover",
    textStyle: { color: isDark ? "#1f2937" : "#333", fontSize: 12 }
  },
  legend: { position: "bottom", textStyle: { fontSize: 11, color: isDark ? "#e5e7eb" : "#1f2937" } },
  chartArea: { left: 0, top: 20, width: "100%", height: "70%", backgroundColor: isDark ? "#1f2937" : "transparent" },
  backgroundColor: isDark ? "#1f2937" : "transparent",
});


export default function WarRoomCharts() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const vehicles = useWarRoomSocket();
  const [activeCity, setActiveCity] = useState<string | null>(null);



  // City centers for proximity assignment
  const cityCenters: Record<string, { lat: number; lng: number }> = {
    Bengaluru: { lat: 12.9716, lng: 77.5946 },
    bangalore: { lat: 12.9716, lng: 77.5946 },
    Mysuru: { lat: 12.2958, lng: 76.6394 },
    Tumakuru: { lat: 13.3409, lng: 77.101 },
    Mandya: { lat: 12.5218, lng: 76.8951 },
    Hassan: { lat: 13.0072, lng: 76.0962 },
    Hyderabad: { lat: 17.385, lng: 78.4867 },
    Chennai: { lat: 13.0827, lng: 80.2707 },
    Delhi: { lat: 28.6139, lng: 77.209 },
    Noida: { lat: 28.5355, lng: 77.3910 },
    Gurugram: { lat: 28.4595, lng: 77.0266 },
    Coimbatore: { lat: 11.0168, lng: 76.9558 },
    Mumbai: { lat: 19.0760, lng: 72.8777 },
  };


  // Normalize city names and handle common aliases
  function normalizeCityName(raw?: string | null): string | null {
    if (!raw) return null;
    const s = String(raw).trim();
    if (!s) return null;
    const key = s.toLowerCase();
    const aliases: Record<string, string> = {
      mysore: "Mysuru",
      mysuru: "Mysuru",
      bengaluru: "Bengaluru",
      bangalore: "Bengaluru",
      "chenna i": "Chennai",
      chennai: "Chennai",
      coimbatore: "Coimbatore",
      mumbai: "Mumbai",
      hyderabad: "Hyderabad",
      delhi: "Delhi",
      noida: "Noida",
      gurugram: "Gurugram",
      mandya: "Mandya",
      hassan: "Hassan",
    };
    if (aliases[key]) return aliases[key];
    return s.charAt(0).toUpperCase() + s.slice(1);
  }


  function getNearestCity(lat: number, lng: number): string | null {
    let minDist = Infinity;
    let nearest = null;
    for (const [city, center] of Object.entries(cityCenters)) {
      const d = Math.sqrt(
        Math.pow(center.lat - lat, 2) + Math.pow(center.lng - lng, 2)
      );
      if (d < minDist) {
        minDist = d;
        nearest = city;
      }
    }
    return nearest;
  }

  // Deduplicate vehicles by unique id
  const uniqueVehicles = useMemo(() => {
    const seen = new Map<string, any>();
    vehicles.forEach((v: any) => {
      const idKey = String(v.vehicleId || v.uniqueId || "");
      if (!idKey) return;
      seen.set(idKey, v);
    });
    return Array.from(seen.values());
  }, [vehicles]);
  // derive normalized city for a vehicle (uses v.city or nearest center fallback)
  function getVehicleCity(v: any): string | null {
    let city = normalizeCityName(v?.city);
    if (!city || city.toLowerCase() === "unknown") {
      if (typeof v?.lat === "number" && typeof v?.lng === "number") {
        const nearest = getNearestCity(v.lat, v.lng);
        if (nearest) city = normalizeCityName(nearest);
      }
    }
    return city;
  }

  const filteredVehicles = useMemo(() => {
    if (!activeCity) return uniqueVehicles;

    return uniqueVehicles.filter((v: any) => {
      const city = getVehicleCity(v);
      return city === activeCity;
    });
  }, [activeCity, uniqueVehicles]);


  // Overall stats (global, always from uniqueVehicles)
  const overallStats = useMemo(() => {
    let opspod = 0;
    let altmobility = 0;
    let moving = 0;
    let idle = 0;
    let ignOff = 0;

    uniqueVehicles.forEach((v: any) => {
      const id = String(v.vehicleId || v.uniqueId || "");
      if (id.startsWith("opspod:")) opspod++;
      else if (id.startsWith("alt:")) altmobility++;

      if (v.speed > 0) moving++;
      else if (v.ignition && v.speed === 0) idle++;
      else if (!v.ignition) ignOff++;
    });

    const total = uniqueVehicles.length;
    return {
      total,
      opspod,
      altmobility,
      moving,
      idle,
      ignOff,
      movingPct: total ? Math.round((moving / total) * 100) : 0,
      idlePct: total ? Math.round((idle / total) * 100) : 0,
      ignOffPct: total ? Math.round((ignOff / total) * 100) : 0,
      opspodPct: total ? Math.round((opspod / total) * 100) : 0,
      altmobilityPct: total ? Math.round((altmobility / total) * 100) : 0,
    };
  }, [uniqueVehicles]);

  // View stats reflect current filtered view (all or selected city)
  const viewStats = useMemo(() => {
    let opspod = 0;
    let altmobility = 0;
    let moving = 0;
    let idle = 0;
    let ignOff = 0;

    filteredVehicles.forEach((v: any) => {
      const id = String(v.vehicleId || v.uniqueId || "");
      if (id.startsWith("opspod:")) opspod++;
      else if (id.startsWith("alt:")) altmobility++;

      if (v.speed > 0) moving++;
      else if (v.ignition && v.speed === 0) idle++;
      else if (!v.ignition) ignOff++;
    });

    const total = filteredVehicles.length;
    return {
      total,
      opspod,
      altmobility,
      moving,
      idle,
      ignOff,
      movingPct: total ? Math.round((moving / total) * 100) : 0,
      idlePct: total ? Math.round((idle / total) * 100) : 0,
      ignOffPct: total ? Math.round((ignOff / total) * 100) : 0,
      opspodPct: total ? Math.round((opspod / total) * 100) : 0,
      altmobilityPct: total ? Math.round((altmobility / total) * 100) : 0,
    };
  }, [filteredVehicles]);


  // City stats with provider breakdown and status breakdown
  const cityStats = useMemo(() => {
    const cityMap: Record<
      string,
      {
        opspod: number;
        altmobility: number;
        total: number;
        moving: number;
        idle: number;
        ignOff: number;
      }
    > = {};

    uniqueVehicles.forEach((v: any) => {
      const city = getVehicleCity(v);
      if (!city) return;

      if (!cityMap[city]) {
        cityMap[city] = { opspod: 0, altmobility: 0, total: 0, moving: 0, idle: 0, ignOff: 0 };
      }

      const id = String(v.vehicleId || v.uniqueId || "");
      if (id.startsWith("opspod:")) cityMap[city].opspod++;
      else if (id.startsWith("alt:")) cityMap[city].altmobility++;

      if (v.speed > 0) cityMap[city].moving++;
      else if (v.ignition && v.speed === 0) cityMap[city].idle++;
      else if (!v.ignition) cityMap[city].ignOff++;

      cityMap[city].total++;
    });

    return cityMap;
  }, [uniqueVehicles]);

  // Sorted cities by total count
  const sortedCities = useMemo(() => {
    return Object.entries(cityStats)
      .filter(([c]) => c !== "Unknown")
      .sort((a, b) => b[1].total - a[1].total);
  }, [cityStats]);

  // Build city-wise status breakdown for tooltips
  const cityStatusBreakdown = useMemo(() => {
    const breakdown: { moving: string[]; idle: string[]; ignOff: string[] } = {
      moving: [],
      idle: [],
      ignOff: [],
    };

    Object.entries(cityStats).forEach(([city, stats]) => {
      if (stats.moving > 0) breakdown.moving.push(`${city}: ${stats.moving}`);
      if (stats.idle > 0) breakdown.idle.push(`${city}: ${stats.idle}`);
      if (stats.ignOff > 0) breakdown.ignOff.push(`${city}: ${stats.ignOff}`);
    });

    // Sort by count (descending)
    breakdown.moving.sort((a: string, b: string) => {
      const countA = parseInt(a.split(": ")[1]);
      const countB = parseInt(b.split(": ")[1]);
      return countB - countA;
    });
    breakdown.idle.sort((a: string, b: string) => {
      const countA = parseInt(a.split(": ")[1]);
      const countB = parseInt(b.split(": ")[1]);
      return countB - countA;
    });
    breakdown.ignOff.sort((a: string, b: string) => {
      const countA = parseInt(a.split(": ")[1]);
      const countB = parseInt(b.split(": ")[1]);
      return countB - countA;
    });

    return breakdown;
  }, [cityStats]);

  function buildStatusTooltipHtml(statusKey: 'moving' | 'idle' | 'ignOff', isDark: boolean) {
    const bgColor = isDark ? "#2d3748" : "#ffffff";
    const textColor = isDark ? "#e2e8f0" : "#1a202c";
    // If a city is active, show only that city's counts
    if (activeCity) {
      const stats = cityStats[activeCity];
      if (!stats) return `<div style="padding:8px; background:${bgColor}; color:${textColor}; border-radius:4px">No vehicles in ${activeCity}</div>`;
      const v = stats[statusKey] || 0;
      return `<div style="padding:10px; min-width:120px; background:${bgColor}; color:${textColor}; border-radius:4px"><b>${activeCity}</b><br/>${statusKey === 'moving' ? 'Moving' : statusKey === 'idle' ? 'Idle' : 'Ign Off'}: ${v}</div>`;
    }

    const lines = cityStatusBreakdown[statusKey];
    if (lines.length === 0) return `<div style="padding:8px; background:${bgColor}; color:${textColor}; border-radius:4px">No vehicles</div>`;
    return `<div style="padding:10px; max-width: 200px; background:${bgColor}; color:${textColor}; border-radius:4px"><b>City-wise Breakdown</b><br/>${lines.join('<br/>')}</div>`;
  }

  // Provider-wise status breakdown
  const providerStatusData = useMemo(() => {
    const opspodStatus = { moving: 0, idle: 0, ignOff: 0, total: 0 };
    const altmobilityStatus = { moving: 0, idle: 0, ignOff: 0, total: 0 };

    filteredVehicles.forEach((v: any) => {
      const id = String(v.vehicleId || v.uniqueId || "");
      const status = v.speed > 0 ? 'moving' : (v.ignition && v.speed === 0 ? 'idle' : 'ignOff');

      if (id.startsWith("opspod:")) {
        opspodStatus[status]++;
        opspodStatus.total++;
      } else if (id.startsWith("alt:")) {
        altmobilityStatus[status]++;
        altmobilityStatus.total++;
      }
    });

    return { opspodStatus, altmobilityStatus };
  }, [filteredVehicles]);

  // OpsPod status pie chart data
  const opspodStatusChartData = useMemo(() => {
    const total = providerStatusData.opspodStatus.total || 1;
    const movingPct = Math.round((providerStatusData.opspodStatus.moving / total) * 100);
    const idlePct = Math.round((providerStatusData.opspodStatus.idle / total) * 100);
    const ignOffPct = Math.round((providerStatusData.opspodStatus.ignOff / total) * 100);
    const bgColor = isDark ? "#2d3748" : "#ffffff";
    const textColor = isDark ? "#e2e8f0" : "#1a202c";
    return [
      ["Status", "Count", { role: 'tooltip', p: { html: true } }],
      ["Moving", providerStatusData.opspodStatus.moving, `<div style="padding:6px; background:${bgColor}; color:${textColor}; border-radius:4px"><b>Moving</b>: ${providerStatusData.opspodStatus.moving} (${movingPct}%)</div>`],
      ["Idle", providerStatusData.opspodStatus.idle, `<div style="padding:6px; background:${bgColor}; color:${textColor}; border-radius:4px"><b>Idle</b>: ${providerStatusData.opspodStatus.idle} (${idlePct}%)</div>`],
      ["Ign Off", providerStatusData.opspodStatus.ignOff, `<div style="padding:6px; background:${bgColor}; color:${textColor}; border-radius:4px"><b>Ign Off</b>: ${providerStatusData.opspodStatus.ignOff} (${ignOffPct}%)</div>`],
    ];
  }, [providerStatusData, isDark]);

  // Alt Mobility status pie chart data
  const altmobilityStatusChartData = useMemo(() => {
    const total = providerStatusData.altmobilityStatus.total || 1;
    const movingPct = Math.round((providerStatusData.altmobilityStatus.moving / total) * 100);
    const idlePct = Math.round((providerStatusData.altmobilityStatus.idle / total) * 100);
    const ignOffPct = Math.round((providerStatusData.altmobilityStatus.ignOff / total) * 100);
    const bgColor = isDark ? "#2d3748" : "#ffffff";
    const textColor = isDark ? "#e2e8f0" : "#1a202c";
    return [
      ["Status", "Count", { role: 'tooltip', p: { html: true } }],
      ["Moving", providerStatusData.altmobilityStatus.moving, `<div style="padding:6px; background:${bgColor}; color:${textColor}; border-radius:4px"><b>Moving</b>: ${providerStatusData.altmobilityStatus.moving} (${movingPct}%)</div>`],
      ["Idle", providerStatusData.altmobilityStatus.idle, `<div style="padding:6px; background:${bgColor}; color:${textColor}; border-radius:4px"><b>Idle</b>: ${providerStatusData.altmobilityStatus.idle} (${idlePct}%)</div>`],
      ["Ign Off", providerStatusData.altmobilityStatus.ignOff, `<div style="padding:6px; background:${bgColor}; color:${textColor}; border-radius:4px"><b>Ign Off</b>: ${providerStatusData.altmobilityStatus.ignOff} (${ignOffPct}%)</div>`],
    ];
  }, [providerStatusData, isDark]);

  // Status distribution data for pie charts with city-wise tooltips
  const statusData = useMemo(() => {
    const total = viewStats.total || 1;
    const movingPct = Math.round((viewStats.moving / total) * 100);
    const idlePct = Math.round((viewStats.idle / total) * 100);
    const ignOffPct = Math.round((viewStats.ignOff / total) * 100);
    
    const bgColor = isDark ? "#2d3748" : "#ffffff";
    const textColor = isDark ? "#e2e8f0" : "#1a202c";
    
    const movingTooltip = activeCity ? `<div style="padding:6px; background:${bgColor}; color:${textColor}; border-radius:4px"><b>Moving</b>: ${viewStats.moving} (${movingPct}%)</div>` : buildStatusTooltipHtml('moving', isDark); 
    const idleTooltip = activeCity ? `<div style="padding:6px; background:${bgColor}; color:${textColor}; border-radius:4px"><b>Idle</b>: ${viewStats.idle} (${idlePct}%)</div>` : buildStatusTooltipHtml('idle', isDark);
    const ignOffTooltip = activeCity ? `<div style="padding:6px; background:${bgColor}; color:${textColor}; border-radius:4px"><b>Ign Off</b>: ${viewStats.ignOff} (${ignOffPct}%)</div>` : buildStatusTooltipHtml('ignOff', isDark);
    
    return [
      ["Status", "Count", { role: 'tooltip', p: { html: true } }],
      ["Moving", viewStats.moving, movingTooltip],
      ["Idle", viewStats.idle, idleTooltip],
      ["Ign Off", viewStats.ignOff, ignOffTooltip],
    ];
  }, [viewStats, activeCity, isDark]);

  const providerData = useMemo(() => {
    const total = providerStatusData.opspodStatus.total + providerStatusData.altmobilityStatus.total || 1;
    const opspodPct = Math.round((providerStatusData.opspodStatus.total / total) * 100);
    const altPct = Math.round((providerStatusData.altmobilityStatus.total / total) * 100);
    const bgColor = isDark ? "#2d3748" : "#ffffff";
    const textColor = isDark ? "#e2e8f0" : "#1a202c";
    return [
      ["Provider", "Count", { role: 'tooltip', p: { html: true } }],
      ["OpsPod", providerStatusData.opspodStatus.total, `<div style="padding:6px; background:${bgColor}; color:${textColor}; border-radius:4px"><b>OpsPod</b>: ${providerStatusData.opspodStatus.total} (${opspodPct}%)</div>`],
      ["Alt Mobility", providerStatusData.altmobilityStatus.total, `<div style="padding:6px; background:${bgColor}; color:${textColor}; border-radius:4px"><b>Alt Mobility</b>: ${providerStatusData.altmobilityStatus.total} (${altPct}%)</div>`],
    ];
  }, [providerStatusData, isDark]);

  return (
    <Box
      sx={{
        p: 2.5,
        minHeight: "100vh",
        backgroundColor: isDark ? "#111827" : "#f9fafb",
        transition: "background-color 0.3s ease",
      }}
    >
      {/* HEADER - Total & City-wise */}
      <Grid container spacing={2} mb={2.5}>
        {/* Total Vehicles Card */}
      <Grid item xs={12} sm={6} md={3}>
  <Paper
    sx={{
      p: 3.5,                 // ⬆️ more padding = larger card
      minHeight: 190,         // ⬆️ taller (not wider)
      borderRadius: 3,
      background: isDark ? "#1f2937" : "white",
      border: isDark ? "1px solid #374151" : "1px solid #e5e7eb",
      boxShadow: isDark ? "0 4px 12px rgba(0,0,0,0.3)" : "0 4px 12px rgba(0,0,0,0.08)",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
    }}
  >
    <Typography
      sx={{
        fontSize: "0.75rem",
        fontWeight: 700,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: isDark ? "#9ca3af" : "#6b7280",
      }}
    >
      Total Pan India Vehicles
    </Typography>

    <Typography
      sx={{
        fontSize: "3rem",     // ⬆️ stronger focus
        fontWeight: 900,
        lineHeight: 1,
        color: isDark ? "#f3f4f6" : "#1a202c",
      }}
    >
      {overallStats.total}
    </Typography>

    <Typography
      sx={{
        fontSize: "0.75rem",
        color: isDark ? "#9ca3af" : "#6b7280",
      }}
    >
      Active fleet
    </Typography>
  </Paper>
</Grid>


        {/* City-wise Summary */}
        <Grid item xs={12} sm={6} md={9}>
          <Paper
            sx={{
              p: 2,
              borderRadius: 2.5,
              background: isDark ? "#1f2937" : "white",
              border: isDark ? "1px solid #374151" : "1px solid #e5e7eb",
              boxShadow: isDark ? "0 4px 12px rgba(0,0,0,0.3)" : "0 4px 12px rgba(0,0,0,0.08)",
            }}
          >
            <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: isDark ? "#f3f4f6" : "#1a202c", mb: 1.5 }}>
              🏙️ CITY-WISE BREAKDOWN
            </Typography>
            <Grid container spacing={1}>
              {sortedCities.map(([city, stats]) => (
                <Grid item xs={6} sm={3} md={2} key={city}>
                  <Box
                    onClick={() => setActiveCity(activeCity === city ? null : city)}
                    role="button"
                    sx={{
                      p: 0.8,
                      borderRadius: 1,
                      background: activeCity === city ? (isDark ? "#065f46" : "#ecfdf5") : (isDark ? "#374151" : "#f9fafb"),
                      border: activeCity === city ? "1px solid #16a34a" : (isDark ? "1px solid #4b5563" : "1px solid #e5e7eb"),
                      textAlign: "center",
                      transition: "all 0.15s ease",
                      cursor: "pointer",
                      color: isDark ? "#f3f4f6" : "#1a202c",
                      "&:hover": {
                        background: isDark ? "#14532d" : "#f0fdf4",
                        borderColor: "#22c55e",
                      },
                    }}
                  >
                    <Typography sx={{ fontSize: "0.65rem", fontWeight: 600, color: "#666" }}>
                      {city}
                    </Typography>
                    <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, color: "#22c55e", mt: 0.2 }}>
                      {stats.total}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* 2x2 GRID - 4 MAIN BOXES */}
      <Grid container spacing={2}>
        {/* BOX 1: Vehicle Status Breakdown - 3D Pie */}
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: 2,
              borderRadius: 2.5,
              background: isDark ? "#1f2937" : "white",
              border: isDark ? "1px solid #374151" : "1px solid #e5e7eb",
              boxShadow: isDark ? "0 4px 12px rgba(0,0,0,0.3)" : "0 4px 12px rgba(0,0,0,0.08)",
            }}
          >
            <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: isDark ? "#f3f4f6" : "#1a202c", mb: 1.5 }}>
              📍 VEHICLE STATUS BREAKDOWN
            </Typography>
            <Chart
              chartType="PieChart"
              width="100%"
              height="350px"
              data={statusData}
              options={{
                ...getPieOptionsShared(isDark),
                slices: {
                  0: { color: STATUS_COLORS.moving },
                  1: { color: STATUS_COLORS.idle },
                  2: { color: STATUS_COLORS.ignOff },
                },
              }}
            />
          </Paper>
        </Grid>

        {/* BOX 2: Provider Distribution - Donut 3D Pie */}
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: 2,
              borderRadius: 2.5,
              background: isDark ? "#1f2937" : "white",
              border: isDark ? "1px solid #374151" : "1px solid #e5e7eb",
              boxShadow: isDark ? "0 4px 12px rgba(0,0,0,0.3)" : "0 4px 12px rgba(0,0,0,0.08)",
            }}
          >
            <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: isDark ? "#f3f4f6" : "#1a202c", mb: 1.5 }}>
              🏢 PROVIDER DISTRIBUTION
            </Typography>
            <Chart
              chartType="PieChart"
              width="100%"
              height="350px"
              data={providerData}
              options={{
                ...getPieOptionsShared(isDark),
                pieHole: 0.4,
                pieSliceTextStyle: { fontSize: 13, color: "#fff", bold: true },
                slices: {
                  0: { color: "#3b82f6", offset: 0.08 },
                  1: { color: "#10b981", offset: 0.08 },
                },
              }}
            />
          </Paper>
        </Grid>

        {/* BOX 3: OpsPod Status - 3D Column/Pie Mix */}
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: 2,
              borderRadius: 2.5,
              background: isDark ? "#1f2937" : "white",
              border: isDark ? "1px solid #374151" : "1px solid #e5e7eb",
              boxShadow: isDark ? "0 4px 12px rgba(0,0,0,0.3)" : "0 4px 12px rgba(0,0,0,0.08)",
            }}
          >
            <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: isDark ? "#f3f4f6" : "#1a202c", mb: 1.5 }}>
              🚙 OPSPOD STATUS
            </Typography>
            <Chart
              chartType="PieChart"
              width="100%"
              height="350px"
              data={opspodStatusChartData}
              options={{
                ...getPieOptionsShared(isDark),
                pieHole: 0.35,
                slices: {
                  0: { color: "#00C853" },
                  1: { color: "#FFD600" },
                  2: { color: "#D50000" },
                },
              }}
            />
          </Paper>
        </Grid>

        {/* BOX 4: Alt Mobility Status - 3D Donut Pie */}
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: 2,
              borderRadius: 2.5,
              background: isDark ? "#1f2937" : "white",
              border: isDark ? "1px solid #374151" : "1px solid #e5e7eb",
              boxShadow: isDark ? "0 4px 12px rgba(0,0,0,0.3)" : "0 4px 12px rgba(0,0,0,0.08)",
            }}
          >
            <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: isDark ? "#f3f4f6" : "#1a202c", mb: 1.5 }}>
              🚐 ALT MOBILITY STATUS
            </Typography>
            <Chart
              chartType="PieChart"
              width="100%"
              height="350px"
              data={altmobilityStatusChartData}
              options={{
                ...getPieOptionsShared(isDark),
                pieHole: 0.35,
                slices: {
                  0: { color: "#00C853", offset: 0.05 },
                  1: { color: "#FFD600", offset: 0.05 },
                  2: { color: "#D50000", offset: 0.05 },
                },
              }}
            />
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}